import { useZero } from "@rocicorp/zero/react";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useEffectEvent } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { useWorkflowZipImport } from "../hooks/useWorkflowZipImport";
import { fetchGalleryWorkflowArtifact } from "../lib/galleryImport";
import { navigate, routes } from "../lib/urls";
import { EmptyState } from "./EmptyState";
import { Spinner } from "./Spinner";
import { useToast } from "./Toast";

export function GalleryImportRoute({
  workflowId,
  commitId,
  sha256,
}: {
  workflowId: string;
  commitId: string;
  sha256: string;
}) {
  const z = useZero();
  const [personalSpace] = useQuery(queries.personalSpace());
  const spaceId = personalSpace?.id ?? null;
  const { importError, importWorkflowZip } = useWorkflowZipImport(spaceId);
  const { showToast } = useToast();
  const importWorkflowZipEvent = useEffectEvent(importWorkflowZip);
  const handleError = useCallback(
    (error: Error) => {
      Sentry.captureException(error, {
        tags: { source: "gallery-import" },
        extra: {
          galleryWorkflowId: workflowId,
          galleryCommitId: commitId,
        },
      });
      showToast({
        icon: "circle-off",
        message: error.message,
        buttonLabel: "Dismiss",
        variant: "error",
      });
      navigate(routes.personal, undefined, { replace: true });
    },
    [commitId, showToast, workflowId]
  );

  useEffect(() => {
    if (!spaceId) return;
    void fetchGalleryWorkflowArtifact({
      workflowId,
      commitId,
      sha256,
    })
      .then(importWorkflowZipEvent)
      .catch((error: unknown) => {
        handleError(
          error instanceof Error
            ? error
            : new Error(
                typeof error === "string" ? error : "Couldn’t import workflow"
              )
        );
        return false;
      })
      .then((imported) => {
        if (imported) {
          z.mutate(
            sharedMutators.recordGalleryWorkflowImported({
              galleryWorkflowId: workflowId,
              galleryCommitId: commitId,
            })
          );
        }
      });
  }, [commitId, handleError, sha256, spaceId, workflowId, z]);

  useEffect(() => {
    if (importError) handleError(new Error(importError));
  }, [handleError, importError]);

  return (
    <EmptyState
      icon={<Spinner size={24} />}
      title="Importing workflow…"
      description="This should only take a moment."
    />
  );
}
